import { useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { ApiError } from '../../lib/api';
import {
  buildCampaignDraftFromRollout,
  createDraft,
  listSavedAudiences,
  useLinkedAdAccount,
} from '../metaAds';
import type { Account } from '../../types/account';
import type { AccountCampaign } from '../../types/accountCampaign';
import type { Campaign } from '../../types/campaign';
import type { FranchiseLocationProfile } from '../../types/franchiseLocationProfile';
import type { FranchiseProfile } from '../../types/franchiseProfile';
import type { Rollout } from '../../types/rollout';

// Build Campaign — Phase 1. The hand-off modal fired from the Meta Build view.
// Fetches the account's DEFAULT saved audiences, then:
//   - zero defaults  → block with a clear prompt + deep-link to Meta Setup (no
//     silent empty build).
//   - has defaults   → preview the seeded ad sets + any non-blocking flags, then
//     "Open pre-filled builder" creates a fresh draft and opens compose in a NEW TAB
//     (the rollout stays open for cross-checking).
// Re-opening always creates a NEW draft (it's a draft, never an auto-publish).
//
// All the rollout→draft mapping lives in buildCampaignDraftFromRollout (pure); this
// component owns only the I/O (fetch audiences, create draft, open the builder) + UX.

type Props = {
  rollout: Rollout;
  row: AccountCampaign;
  account: Account;
  locationProfile: FranchiseLocationProfile | null;
  brandFranchiseProfile: FranchiseProfile | null;
  campaign: Campaign | null;
  onClose: () => void;
};

export function BuildCampaignModal({
  rollout,
  row,
  account,
  locationProfile,
  brandFranchiseProfile,
  campaign,
  onClose,
}: Props) {
  const navigate = useNavigate();

  const audiencesQuery = useQuery({
    queryKey: ['account', account.id, 'saved-audiences'],
    queryFn: () => listSavedAudiences(account.id),
  });

  // The linked ad account's timezone — the basis the builder resolves its schedule
  // against (and displays). The rollout's UTC start/end are converted into THIS tz
  // before seeding so the builder shows the same wall-clock as the rollout. Falls back
  // to the browser's tz when the ad account is unknown (best-effort display match).
  const adAccount = useLinkedAdAccount(account.id);
  const scheduleTimeZone =
    adAccount.timezoneName ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;

  const defaultAudiences = useMemo(
    () => (audiencesQuery.data?.savedAudiences ?? []).filter((a) => a.isAccountDefault),
    [audiencesQuery.data],
  );

  // Build the seeded draft input as soon as the defaults + ad-account tz are known so
  // we can show the ad-set names + flags before the AM commits. Pure + cheap → useMemo.
  const built = useMemo(() => {
    if (!audiencesQuery.data || adAccount.isLoading || defaultAudiences.length === 0) return null;
    return buildCampaignDraftFromRollout({
      rollout,
      row,
      account,
      campaign,
      locationProfile,
      brandProfile: brandFranchiseProfile,
      defaultAudiences,
      scheduleTimeZone,
    });
  }, [
    audiencesQuery.data,
    adAccount.isLoading,
    scheduleTimeZone,
    defaultAudiences,
    rollout,
    row,
    account,
    campaign,
    locationProfile,
    brandFranchiseProfile,
  ]);

  // Open the builder in a NEW tab so the rollout stays put for cross-checking. The
  // tab is opened SYNCHRONOUSLY in the click handler (about:blank) and pointed at the
  // draft once it's created — opening it after the async createDraft would be popup-
  // blocked. The opened window rides through as the mutation variable.
  const createMut = useMutation({
    mutationFn: (_win: Window | null) => createDraft(built!.input),
    onSuccess: ({ draft }, win) => {
      const path = `/advertising/meta/compose/${draft.id}`;
      const url = `${window.location.origin}${path}`;
      if (win && !win.closed) {
        win.location.href = url; // the pre-opened tab → the builder
      } else {
        // Popup blocked (no window): try a direct open; if THAT's blocked too, fall
        // back to navigating the current tab so the AM isn't stuck.
        const opened = window.open(url, '_blank');
        if (!opened) navigate(path);
      }
      onClose(); // close the modal; the rollout tab stays where it is
    },
    onError: (_e, win) => {
      // Don't leave a blank/errored tab open — the error renders in the rollout tab.
      if (win && !win.closed) win.close();
    },
  });
  const busy = createMut.isPending;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div
        className="w-full max-w-lg rounded-md bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="build-campaign-title"
      >
        <div className="p-6 space-y-4">
          <h2 id="build-campaign-title" className="text-lg font-semibold text-slate-900">
            Build Campaign — {account.name}
          </h2>

          {(audiencesQuery.isLoading ||
            (audiencesQuery.data && defaultAudiences.length > 0 && adAccount.isLoading)) && (
            <CenteredSpinner />
          )}

          {audiencesQuery.error && (
            <p className="text-sm text-red-600">
              {audiencesQuery.error instanceof ApiError
                ? audiencesQuery.error.message
                : "Couldn't load this account's saved audiences."}
            </p>
          )}

          {audiencesQuery.data && defaultAudiences.length === 0 && (
            <div className="space-y-3">
              <p className="text-sm text-slate-700">
                This account has no <strong>Account-Default</strong> audiences set. A
                campaign needs at least one default audience to seed an ad set.
              </p>
              <p className="text-sm text-slate-600">
                Set defaults in Meta Setup → Audiences first, then come back and build.
              </p>
            </div>
          )}

          {built && (
            <div className="space-y-4">
              <div className="space-y-1">
                <p className="text-sm text-slate-700">
                  Opens the Meta Ad Builder pre-filled from this rollout — CBO campaign
                  (budget, dates, copy, name), pixel + lead form / website destination
                  from the account defaults, and manual placements. No images yet
                  (add creative in the builder).
                </p>
              </div>

              <div className="space-y-1">
                <p className="text-xs uppercase tracking-wide text-slate-500">
                  {built.input.adSets.length} ad set
                  {built.input.adSets.length === 1 ? '' : 's'} — one per default audience
                </p>
                <ul className="list-disc pl-5 text-sm text-slate-700">
                  {built.input.adSets.map((s) => (
                    <li key={s.id}>{s.name || 'Unnamed audience'}</li>
                  ))}
                </ul>
              </div>

              {built.warnings.length > 0 && (
                <div className="rounded border border-amber-200 bg-amber-50 p-3 space-y-1">
                  <p className="text-xs uppercase tracking-wide font-semibold text-amber-700">
                    Heads up — finish these in the builder
                  </p>
                  <ul className="list-disc pl-5 text-sm text-amber-800">
                    {built.warnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}

              {createMut.error && (
                <p className="text-sm text-red-600">
                  {createMut.error instanceof ApiError
                    ? createMut.error.message
                    : "Couldn't create the draft. Try again."}
                </p>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {built ? 'Cancel' : 'Close'}
            </button>

            {audiencesQuery.data && defaultAudiences.length === 0 && (
              <Button asChild>
                <Link to={`/advertising/meta-setup?account=${account.id}&tab=audiences`}>
                  Set defaults in Meta Setup
                </Link>
              </Button>
            )}

            {built && (
              <Button
                onClick={() => createMut.mutate(window.open('about:blank', '_blank'))}
                disabled={busy}
              >
                {busy ? 'Opening builder…' : 'Open pre-filled builder'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
